import { ArrowRight, CornerDownRight, LogIn, Flag } from "lucide-react";

export const CH_COLORS = { Meeting: "#60a5fa", Call: "#f59e0b", WhatsApp: "#22c55e", Khush: "#e879f9", Email: "#38bdf8", Task: "#a3a3a3" };
export const effectOf = (sop, k, o) => (sop.effects || {})[`${k}:${o}`] || (sop.effects || {})[o] || { type: "none" };
export const trackName = (sop, k) => sop.tracks[k]?.name || k;

export function effLabel(sop, k, e) {
  if (e.type === "track") return `${e.stage || sop.tracks[e.track]?.stage || ""} · ${trackName(sop, e.track)}`;
  if (e.type === "next") return `Next step${e.stage ? ` · ${e.stage}` : ""}`;
  if (e.type === "rebook") return "Rebook → new meeting";
  if (e.type === "client_date") return "Wait for client's date";
  if (e.type === "close") return `${e.stage} · ${e.reason === "pick" ? "pick reason" : e.reason}`;
  return "Log only";
}

function Branches({ sop, k }) {
  const outs = sop.outcomes[k] || [];
  if (!outs.length) return <div className="text-[11px] text-neutral-600">No outcomes: tick the task done.</div>;
  return (
    <div className="space-y-1">
      {outs.map((o) => {
        const e = effectOf(sop, k, o);
        const tone = e.type === "close" ? "text-[var(--breach)]" : e.type === "track" ? "text-white" : "text-neutral-400";
        return (
          <div key={o} className="text-[11px] leading-snug" data-testid={`flow-${sop._id}-${k}-branch`}>
            <div className="flex items-start gap-1.5"><CornerDownRight size={11} className="text-neutral-600 shrink-0 mt-0.5" /><span className="text-neutral-300 break-words">{o}</span></div>
            <div className="flex items-start gap-1 pl-4"><ArrowRight size={10} className="text-neutral-600 shrink-0 mt-0.5" /><span className={`break-words min-w-0 ${tone}`}>{effLabel(sop, k, e)}</span></div>
          </div>
        );
      })}
    </div>
  );
}

function Lane({ sop, k, entry }) {
  const t = sop.tracks[k];
  return (
    <div className={`panel p-3 min-w-[290px] w-[290px] shrink-0 space-y-3 ${entry ? "border-white/40" : ""}`} data-testid={`flow-lane-${k}`}>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">{entry ? "Entry · meeting" : "Track"}</div>
        <div className="text-sm font-medium">{t.name || k}</div>
        <div className="text-[11px] text-neutral-500">Stage: {t.stage}</div>
      </div>
      <ol className="space-y-1">
        {t.steps.map((s, i) => (
          <li key={i} className="flex items-start gap-2 text-xs panel-2 px-2 py-1.5 leading-snug">
            <span className="font-mono text-[10px] text-neutral-500 w-8 shrink-0 mt-0.5">D+{s.day}</span>
            <span className="w-1.5 h-1.5 rounded-full shrink-0 mt-1.5" style={{ background: CH_COLORS[s.channel] || "#888" }} />
            <span className="flex-1 break-words min-w-0">{s.label}<span className="block text-[10px] text-neutral-500">{s.channel}</span></span>
            <span className="text-[10px] text-neutral-500 shrink-0">{s.owner === "khush" ? "Khush" : "Rep"}</span>
          </li>
        ))}
      </ol>
      {t.exit_stage && <div className="text-[11px] flex items-center gap-1 text-neutral-400"><Flag size={11} />After last: {t.exit_stage} · {t.exit_reason}</div>}
      <div className="border-t border-[var(--border)] pt-2"><div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Outcomes</div><Branches sop={sop} k={k} /></div>
    </div>
  );
}

export default function SopFlow({ sop, entries = [] }) {
  const others = Object.keys(sop.tracks).filter((k) => k !== "meeting");
  return (
    <div className="space-y-3" data-testid="sop-flow">
      <div className="flex items-stretch gap-3 overflow-x-auto pb-3">
        <div className="panel p-3 min-w-[230px] w-[230px] shrink-0 space-y-2" data-testid="flow-entry">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 flex items-center gap-1"><LogIn size={11} />Enters from</div>
          {entries.length ? entries.map((x) => <div key={x} className="text-xs panel-2 px-2 py-1 break-words">{x}</div>)
            : <div className="text-[11px] text-neutral-600">No form ending routes here yet. Set it in Form → Ending → "Send lead to".</div>}
          <div className="text-[11px] text-neutral-500 pt-1">Or a Calendly booking, import, or admin "Change SOP".</div>
        </div>
        <div className="flex items-center text-neutral-600"><ArrowRight size={16} /></div>
        <Lane sop={sop} k="meeting" entry />
        <div className="flex items-center text-neutral-600"><ArrowRight size={16} /></div>
        {others.map((k) => <Lane key={k} sop={sop} k={k} />)}
        {(sop.outcomes.closed || []).length > 0 && (
          <div className="panel p-3 min-w-[240px] w-[240px] shrink-0 space-y-2" data-testid="flow-lane-closed">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Lost / Shelved</div>
            <div className="text-[11px] text-neutral-500">Revive with:</div><Branches sop={sop} k="closed" />
          </div>
        )}
      </div>
    </div>
  );
}
